import React, { useState, useEffect, useMemo, useContext } from "react";
import { useHistory } from "react-router-dom";
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Avatar,
  Chip,
  Alert,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  Backdrop,
  CircularProgress,
  Snackbar,
  IconButton,
  Tooltip,
  Badge,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  Category as CategoryIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  ShoppingBasket as BasketIcon,
} from '@mui/icons-material';
import { IonContent, IonPage } from '@ionic/react';
import { format } from 'date-fns';

// Context
import CartContext from "../../../context/CartContext/CartContext";

// Utils
import { baseUrl, config, requestID } from "../../../common/utils";
import { formatCurrencyN } from "../../../common/utils_t";

// Types
interface Product {
  id?: number;
  product_id: string;
  name: string;
  qty: number;
  img_src?: string;
  productCategory?: string;
  category?: string;
}

interface Category {
  category_id: string;
  name: string;
}

interface Waiter {
  user_id: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  role?: string;
  fullName?: string;
}

interface CartItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  image?: string;
  available_qty?: number;
}

const DispenseStockFb: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  
  // Context - removed price-related items
  const {
    items,
    totalAmount,
    addItem,
    removeItem,
    clearCart,
  } = useContext(CartContext);
  
  // Product states
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  
  // Waiter states
  const [waiters, setWaiters] = useState<Waiter[]>([]);
  const [selectedWaiter, setSelectedWaiter] = useState<Waiter | null>(null);
  const [loadingWaiters, setLoadingWaiters] = useState<boolean>(false);
  
  // UI states
  const [loading, setLoading] = useState<boolean>(false);
  const [processingOrder, setProcessingOrder] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  
  // Dialog states
  const [showWaiterDialog, setShowWaiterDialog] = useState<boolean>(false);
  const [showProcessDialog, setShowProcessDialog] = useState<boolean>(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Helper function to get available quantity for a product
  const getAvailableQuantity = (productId: string): number => {
    const product = allProducts.find(p => p.product_id === productId);
    return product ? product.qty : 0;
  };

  // Helper function to get current cart quantity for a product
  const getCartQuantity = (productId: string): number => {
    const cartItem = items.find((item: CartItem) => item.product_id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Helper function to get remaining available quantity for a product
  const getRemainingQuantity = (productId: string): number => {
    const availableQty = getAvailableQuantity(productId);
    const cartQty = getCartQuantity(productId);
    return Math.max(0, availableQty - cartQty);
  };

  // Filtered products based on category and search
  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) {
      return [];
    }
    return products.filter((product) => {
      const matchesCategory = selectedCategory
        ? String(product.productCategory) === String(selectedCategory)
        : true;
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchQuery]);

  // Handle add to cart
  const handleAddToCart = (product: Product) => {
    if (!selectedWaiter) {
      showNotification("Please select a housekeeper first", "warning");
      setShowWaiterDialog(true);
      return;
    }

    // Check available quantity
    const availableQty = getRemainingQuantity(product.product_id);
    if (availableQty <= 0) {
      showNotification(`Cannot add ${product.name}. Only ${getAvailableQuantity(product.product_id)} available in stock.`, "error");
      return;
    }
    
    addItem({
      id: product.product_id,
      product_name: product.name,
      quantity: 1,
      product_id: product.product_id,
      image: product.img_src,
      available_qty: product.qty,
    });
  };

  // Cart item handlers
  const cartItemRemoveHandler = (id: string) => {
    removeItem(id);
  };

  const cartItemAddHandler = (item: CartItem) => {
    if (!selectedWaiter) {
      showNotification("Please select a housekeeper first", "warning");
      setShowWaiterDialog(true);
      return;
    }

    // Check if we can add more of this item
    const availableQty = getRemainingQuantity(item.product_id);
    if (availableQty <= 0) {
      showNotification(`Cannot add more ${item.product_name}. Only ${getAvailableQuantity(item.product_id)} available in stock.`, "error");
      return;
    }
    
    addItem({ ...item, quantity: 1 });
  };

  // Fetch waiters - DIRECT API CALL
  const fetchWaiters = async () => {
    setLoadingWaiters(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        `${baseUrl()}Housekeepers/getHousekeepers`,
        formData,
        config
      );

      console.log("Housekeepers response:", response.data);

      if (response.data.status) {
        const waitersData = response.data.data || [];
        // Format full name for each waiter
        const formattedWaiters = waitersData.map((waiter: any) => ({
          ...waiter,
          fullName: `${waiter.firstName || ''} ${waiter.middleName ? waiter.middleName + ' ' : ''}${waiter.lastName || ''}`.trim()
        }));
        setWaiters(formattedWaiters);
      } else {
        showNotification("Failed to load housekeepers", "error");
      }
    } catch (error) {
      console.error("Error fetching housekeepers:", error);
      showNotification("Failed to load housekeepers", "error");
    } finally {
      setLoadingWaiters(false);
    }
  };

  // Fetch products - DIRECT API CALL
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        `${baseUrl()}warehouses/getBarInventoryByBranch_less_rooms_category`,
        formData,
        config
      );

      console.log("Products response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching products:", error);
      showNotification("Failed to load products", "error");
      return { data: [] };
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories - DIRECT API CALL
  const fetchCategories = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        `${baseUrl()}warehouses/getCategories`,
        formData,
        config
      );

      console.log("Categories response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching categories:", error);
      return { data: [] };
    }
  };

  // Create customer order - DIRECT API CALL
  const createCustomerOrder = async (orderData: any) => {
    try {
      const payload = {
        requestID: requestID(),
        ...orderData
      };

      const response = await axios.post(
        `${baseUrl()}BeverageSupervisors/dispenseHousekeepers`,
        payload,
        config
      );

      console.log("Order response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Error creating order:", error);
      return error.response?.data || { status: false, message: "Failed to create order" };
    }
  };

  // Fetch initial data (products and categories)
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        fetchProducts(),
        fetchCategories(),
      ]);

      // Map categories to expected format
      const categoriesData = categoriesRes.data || [];
      setCategories(categoriesData);

      // Map products with category names
      const productsWithCategoryName = (productsRes.data || []).map((p: any) => {
        const category = categoriesData.find((c: any) => c.category_id === p.productCategory);
        return { ...p, category: category ? category.name : 'Unknown' };
      });

      setAllProducts(productsWithCategoryName);
      setProducts(productsWithCategoryName);
      
    } catch (error) {
      console.error("Error fetching initial data:", error);
      showNotification("Failed to load products", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle create order - updated to not include price
  const handleCreateOrder = async () => {
    if (!selectedWaiter) {
      showNotification("Please select a housekeeper", "warning");
      setShowWaiterDialog(true);
      return;
    }

    if (items.length === 0) {
      showNotification("Please add items to cart", "warning");
      return;
    }

    setProcessingOrder(true);
    
    // Prepare items without price for dispensing
    const dispenseItems = items.map((item: CartItem) => ({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity
    }));

    const dispenseData = {
      housekeeper_id: selectedWaiter?.user_id || null,
      housekeeper_name: selectedWaiter?.fullName || `${selectedWaiter?.firstName} ${selectedWaiter?.lastName}`,
      items: dispenseItems,
      notes: "Stock dispensed to housekeeper"
    };

    try {
      const res = await createCustomerOrder(dispenseData);

      if (res.status === true) {
        showNotification("Stock dispensed successfully!", "success");
        setShowProcessDialog(true);
        clearCart();
        await fetchInitialData(); // Refresh product quantities
      } else {
        showNotification("Failed to dispense stock. " + (res.message || ""), "error");
      }
    } catch (error: any) {
      console.error("Error dispensing stock:", error);
      const errorMessage = error.response?.data?.message || "Failed to dispense stock. Please try again.";
      showNotification(errorMessage, "error");
    } finally {
      setProcessingOrder(false);
    }
  };

  // Handle waiter selection
  const handleWaiterSelect = (waiter: Waiter) => {
    setSelectedWaiter(waiter);
    setShowWaiterDialog(false);
    showNotification(`Selected: ${waiter.fullName || `${waiter.firstName} ${waiter.lastName}`}`, "success");
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchInitialData(), fetchWaiters()]);
    setRefreshing(false);
  };

  // Initial load
  useEffect(() => {
    fetchInitialData();
    fetchWaiters();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const getFullName = (person: Waiter): string => {
    return person.fullName || `${person.firstName} ${person.middleName ? person.middleName + ' ' : ''}${person.lastName}`.trim();
  };

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
          
          {/* Header */}
          <Paper 
            elevation={0}
            sx={{ 
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="xl">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <IconButton onClick={() => history.goBack()}>
                  <BackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <BasketIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Dispense Stock
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select products to dispense to housekeeper
                  </Typography>
                </Box>
                
                {/* Housekeeper Selection Button */}
                <Tooltip title="Select Housekeeper">
                  <Button
                    variant={selectedWaiter ? "contained" : "outlined"}
                    color="primary"
                    startIcon={<PeopleIcon />}
                    onClick={() => setShowWaiterDialog(true)}
                    sx={{ mr: 1 }}
                  >
                    {selectedWaiter ? getFullName(selectedWaiter) : "Select Housekeeper"}
                  </Button>
                </Tooltip>
                
                <Tooltip title="Refresh">
                  <IconButton onClick={handleRefresh} disabled={refreshing}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Container>
          </Paper>

          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Grid container spacing={3}>
              {/* Product Selection Panel - Left Side */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, minHeight: 'calc(100vh - 200px)' }}>
                  {/* Search and Categories */}
                  <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      placeholder="Search products..."
                      size="small"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Stack>

                  {/* Category Tabs */}
                  <Tabs
                    value={selectedCategory}
                    onChange={(e, newValue) => setSelectedCategory(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Tab label="All" value={null} />
                    {categories.map((category) => (
                      <Tab
                        key={category.category_id}
                        label={category.name}
                        value={category.category_id}
                      />
                    ))}
                  </Tabs>

                  {/* Product Grid - Price removed */}
                  {loading ? (
                    <Grid container spacing={2}>
                      {Array.from({ length: 8 }).map((_, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Paper sx={{ p: 2, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : filteredProducts.length > 0 ? (
                    <Grid container spacing={2}>
                      {filteredProducts.map((product) => {
                        const remainingQty = getRemainingQuantity(product.product_id);
                        const isOutOfStock = remainingQty <= 0;
                        
                        return (
                          <Grid item xs={6} sm={4} md={3} key={product.product_id}>
                            <Paper
                              onClick={() => !isOutOfStock && handleAddToCart(product)}
                              sx={{
                                p: 2,
                                cursor: isOutOfStock || !selectedWaiter ? 'not-allowed' : 'pointer',
                                opacity: isOutOfStock ? 0.5 : 1,
                                transition: 'transform 0.2s',
                                border: `1px solid ${theme.palette.divider}`,
                                '&:hover': {
                                  transform: !isOutOfStock && selectedWaiter ? 'scale(1.02)' : 'none',
                                  boxShadow: !isOutOfStock && selectedWaiter ? theme.shadows[4] : 'none',
                                },
                              }}
                            >
                              <Stack spacing={1} alignItems="center">
                                <Avatar
                                  src={product.img_src || '/placeholder.svg'}
                                  alt={product.name}
                                  sx={{ width: 80, height: 80 }}
                                />
                                <Typography variant="subtitle2" align="center" fontWeight={600}>
                                  {product.name}
                                </Typography>
                                <Chip
                                  label={isOutOfStock ? "Out of Stock" : `${remainingQty} available`}
                                  size="small"
                                  color={isOutOfStock ? "error" : "success"}
                                  variant="outlined"
                                />
                              </Stack>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <InventoryIcon sx={{ fontSize: 64, color: theme.palette.grey[300] }} />
                      <Typography color="text.secondary">No products found</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Cart Panel - Right Side */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
                  {/* Cart Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      <Badge badgeContent={items.length} color="primary" sx={{ mr: 1 }}>
                        <CartIcon />
                      </Badge>
                      Current Dispense List
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={clearCart}
                      disabled={items.length === 0}
                    >
                      Clear
                    </Button>
                  </Stack>

                  <Divider />

                  {/* Cart Items - Price removed */}
                  <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
                    {items.length === 0 ? (
                      <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                        <CartIcon sx={{ fontSize: 64, color: theme.palette.grey[300] }} />
                        <Typography color="text.secondary">Your dispense list is empty</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Select products to dispense to housekeeper
                        </Typography>
                      </Stack>
                    ) : (
                      <List disablePadding>
                        {items.map((item: CartItem) => {
                          const remainingQty = getRemainingQuantity(item.product_id);
                          
                          return (
                            <ListItem
                              key={item.product_id}
                              sx={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                py: 1,
                                borderBottom: `1px dashed ${theme.palette.divider}`,
                              }}
                            >
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                  src={item.image || '/placeholder.svg'}
                                  sx={{ width: 40, height: 40 }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {item.product_name}
                                  </Typography>
                                </Box>

                                {/* Quantity Controls */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => cartItemRemoveHandler(item.product_id)}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography>{item.quantity}</Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => cartItemAddHandler(item)}
                                    disabled={remainingQty <= 0}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>

                              {/* Stock Warning */}
                              {remainingQty <= 0 && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                  Maximum quantity reached
                                </Typography>
                              )}
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Box>

                  <Divider />

                  {/* Cart Summary - Total quantity removed */}
                  <Box sx={{ pt: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Total Items:</Typography>
                      <Typography variant="h6" color="primary.main" fontWeight={700}>
                        {items.length}
                      </Typography>
                    </Stack>

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleCreateOrder}
                      disabled={items.length === 0 || processingOrder || !selectedWaiter}
                      startIcon={processingOrder ? <CircularProgress size={20} /> : <ReceiptIcon />}
                      sx={{ py: 1.5 }}
                    >
                      {processingOrder ? "Processing..." : "Dispense Stock"}
                    </Button>

                    {!selectedWaiter && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Please select a housekeeper to continue
                      </Alert>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>

          {/* Housekeeper Selection Dialog */}
          <Dialog open={showWaiterDialog} onClose={() => setShowWaiterDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PeopleIcon color="primary" />
                <Typography variant="h6">Select Housekeeper</Typography>
              </Stack>
              <IconButton
                sx={{ position: 'absolute', right: 8, top: 8 }}
                onClick={() => setShowWaiterDialog(false)}
              >
                <CancelIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {loadingWaiters ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : waiters.length > 0 ? (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Select Housekeeper</InputLabel>
                  <Select
                    value={selectedWaiter?.user_id || ""}
                    label="Select Housekeeper"
                    onChange={(e) => {
                      const waiterId = e.target.value;
                      const selected = waiters.find(w => w.user_id === waiterId);
                      if (selected) {
                        handleWaiterSelect(selected);
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a housekeeper</em>
                    </MenuItem>
                    {waiters.map((waiter) => (
                      <MenuItem key={waiter.user_id} value={waiter.user_id}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                            <PeopleIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body1">
                              {getFullName(waiter)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {waiter.role === 'waiter' ? 'Waiter' : (waiter.role === 'steward' ? 'Steward' : 'Staff')}
                            </Typography>
                          </Box>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <PeopleIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                  <Typography color="text.secondary">No housekeeper found</Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowWaiterDialog(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>

          {/* Success Dialog */}
          <Dialog open={showProcessDialog} onClose={() => setShowProcessDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ReceiptIcon color="success" />
                <Typography variant="h6">Stock Dispensed Successfully</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Alert severity="success" sx={{ mb: 2 }}>
                Stock has been dispensed successfully!
              </Alert>
              <Typography variant="body2" color="text.secondary">
                You can continue dispensing more stock.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowProcessDialog(false)} variant="outlined">
                Continue Dispensing
              </Button>
            </DialogActions>
          </Dialog>

          {/* Refresh Indicator */}
          {refreshing && (
            <Box sx={{ position: 'fixed', top: 80, right: 24, zIndex: 1100 }}>
              <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} color="inherit" />
                  <Typography variant="caption">Refreshing...</Typography>
                </Stack>
              </Paper>
            </Box>
          )}

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
              {snackbar.message}
            </Alert>
          </Snackbar>

        </Box>
      </IonContent>
    </IonPage>
  );
};

export default DispenseStockFb;